import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  DeleteForever as DeleteIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  Person as CustomerIcon,
  History as HistoryIcon
} from '@mui/icons-material';

const DeleteBusinessDialog = ({ open, onClose, onConfirm, businessName, loading }) => {
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');

  const isConfirmValid = confirmText === businessName;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isConfirmValid) {
      setError('Business name does not match. Please type it exactly as shown.');
      return;
    }

    setError('');
    try {
      await onConfirm();
      setConfirmText('');
    } catch (err) {
      setError(err.message || 'Failed to delete business');
    }
  };

  const handleClose = () => {
    if (!loading) {
      setConfirmText('');
      setError('');
      onClose();
    }
  };

  const dataToDelete = [
    { icon: <PeopleIcon />, text: 'All users and their login history' },
    { icon: <StoreIcon />, text: 'All branches and settings' },
    { icon: <InventoryIcon />, text: 'All products and inventory records' },
    { icon: <ReceiptIcon />, text: 'All sales transactions and reports' },
    { icon: <CustomerIcon />, text: 'All customer data' },
    { icon: <HistoryIcon />, text: 'All activity logs and audit trails' }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: 'error.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <DeleteIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
              Delete Business Permanently
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Alert
            severity="error"
            icon={<WarningIcon />}
            sx={{
              mb: 3,
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              WARNING: This will permanently delete all data for "{businessName}"
            </Typography>
            <Typography variant="body2">
              This action is <strong>irreversible</strong>. All associated data will be permanently removed from the system.
            </Typography>
          </Alert>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            The following data will be permanently deleted:
          </Typography>

          <List dense sx={{ bgcolor: 'grey.50', borderRadius: 2, mb: 3 }}>
            {dataToDelete.map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36, color: 'error.main' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>

          <Typography variant="body2" sx={{ mb: 2 }}>
            To confirm deletion, please type the business name exactly as shown:
          </Typography>

          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.100',
              borderRadius: 2,
              mb: 2,
              textAlign: 'center'
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontFamily: 'monospace',
                wordBreak: 'break-all'
              }}
            >
              {businessName}
            </Typography>
          </Box>

          <TextField
            label="Type business name to confirm"
            placeholder={businessName}
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={loading}
            error={confirmText.length > 0 && !isConfirmValid}
            helperText={
              confirmText.length > 0 && !isConfirmValid
                ? 'Business name does not match'
                : ''
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: isConfirmValid ? 'error.main' : undefined
                }
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="error"
            disabled={loading || !isConfirmValid}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
            sx={{ borderRadius: 2 }}
          >
            {loading ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DeleteBusinessDialog;
